import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Loader2, CalendarDays } from "lucide-react";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import type { TrialProspect } from "@/services/edgeFunctionWrappers/trialProspectsWrapper";
import { TrialSlotCalendar, type TrialSlotSelection } from "@/components/trials/TrialSlotCalendar";

interface Props {
  prospect: TrialProspect | null;
  onClose: () => void;
  onDone: (new_class_id: string, new_class_date: string) => void;
  submitting?: boolean;
}

export function RescheduleTrialDialog({ prospect, onClose, onDone, submitting }: Props) {
  const { t } = useTranslation("trials");
  const [selected, setSelected] = useState<TrialSlotSelection | null>(null);

  // reset on prospect change
  useEffect(() => {
    if (prospect) {
      setSelected(null);
    }
  }, [prospect?.id]);

  if (!prospect) return null;

  const currentLabel = `${prospect.class_date}${prospect.time ? ` · ${prospect.time}` : ""}${prospect.scheduled_classes?.name ? ` · ${prospect.scheduled_classes.name}` : ""}`;

  const handleConfirm = () => {
    if (!selected) return;
    onDone(selected.classId, selected.classDate);
  };

  return (
    <Dialog open={!!prospect} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="w-[calc(100vw-1rem)] sm:max-w-3xl max-h-[90vh] flex flex-col">
        <DialogHeader>
          <DialogTitle>{t("rescheduleDialog.title")} — {prospect.first_name} {prospect.last_name}</DialogTitle>
          <DialogDescription className="flex items-center gap-1.5 text-xs">
            <CalendarDays className="h-3 w-3" />
            <span>{t("rescheduleDialog.currentSlot", { defaultValue: "Créneau actuel" })} : {currentLabel}</span>
          </DialogDescription>
        </DialogHeader>

        <TrialSlotCalendar
          value={selected}
          onChange={setSelected}
          initialDate={new Date(`${prospect.class_date}T12:00:00`)}
          currentSlot={{ classId: prospect.class_id, classDate: prospect.class_date }}
        />

        <DialogFooter className="flex-col-reverse sm:flex-row gap-2">
          <Button variant="outline" onClick={onClose} className="w-full sm:w-auto">
            {t("cancel")}
          </Button>
          <Button
            disabled={!selected || submitting}
            onClick={handleConfirm}
            className="w-full sm:w-auto"
          >
            {submitting && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
            {t("rescheduleDialog.confirm")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
